import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {
    SelectItem,
    SelectTrigger,
    SelectVirtualList,
    SelectComboboxPopover,
    SelectComboboxInput,
    SelectComboboxList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {eventTypeMapToLabel} from '@/features/agenda/utils'
import {ToggleGroupListType} from '@/components/commons/toggle-group-list-type/ToggleGroupListType'
import {useInputSearch} from '@/hooks/useInputSearch'
import {EventCategory} from '@/features/agenda/types'
import {AccessFrequency, PlannedAccesses} from '@/features/planned-accesses/types'
import {AccessGroupNameEnum, AccessType, AccessTypeCategoryEnum, AccessTypeKeyName} from '@/features/access-types/types'
import {useGetAccessTypes} from '@/features/access-types/queries/useGetAccessTypes'
import {useGetPlannedAccessTypes} from '@/features/planned-accesses/queries/useGetPlannedAccessTypes'

type InputSelectPlannedAccessValue =
    | (Pick<AccessType, 'id' | 'accessType'> & {
          frequency?: AccessFrequency | null
          category?: AccessTypeCategoryEnum
      })
    | null

interface InputSelectPlannedAccessProps {
    disabled?: boolean
    label?: string
    category?: EventCategory
    groupName?: AccessGroupNameEnum
    specializationId?: string
    value?: InputSelectPlannedAccessValue
    onChange: (value: InputSelectPlannedAccessValue) => void
    disabledAccessTypes?: Pick<AccessType, 'id'>[]
    disabledAccessTypeKeyNames?: AccessTypeKeyName[]
    projectId?: string
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
}

type ListType = 'planned' | 'all'

export const InputSelectPlannedAccess = forwardRef<HTMLSelectElement, InputSelectPlannedAccessProps>(
    (
        {
            category,
            groupName,
            label,
            onChange,
            errorMessage,
            disabled,
            placeholder,
            value,
            onClear,
            disabledAccessTypes,
            disabledAccessTypeKeyNames,
            projectId,
            specializationId
        },
        ref
    ) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const [isPopoverOpen, setIsPopoverOpen] = useState(false)
        const inputUserRef = useRef(null)
        const [listType, setListType] = useState<ListType>(projectId ? 'planned' : 'all')
        const {searchValue, onSearch, onResetSearch} = useInputSearch()

        const {
            data: allPlannedAccesses,
            isLoading: isLoadingAllPlannedAccesses,
            isError: isErrorAllPlannedAccesses
        } = useGetPlannedAccessTypes({
            urlParams: {
                projectId: projectId ?? ''
            },
            params: {
                search: searchValue ?? '',
                specializationId,
                groupName,
                category,
                page: 1,
                limit: 20
            },
            options: {
                enabled: !!projectId && isQueryEnabled && listType == 'planned'
            }
        })

        const {
            data: allAccessTypes,
            isLoading: isLoadingAllAccessTypes,
            isError: isErrorAllAccessTypes
        } = useGetAccessTypes({
            params: {
                search: searchValue ?? '',
                specializationId,
                groupName,
                category,
                limit: 20,
                page: 1
            },
            options: {
                enabled: isQueryEnabled && listType == 'all'
            }
        })

        const allCategoryAccessTypes = useMemo(() => {
            return allAccessTypes?.pages.flatMap(page => page.data)
        }, [allAccessTypes, searchValue, category])

        const plannedAccesses = useMemo(() => {
            return allPlannedAccesses?.pages.flatMap(page => page.data)
        }, [allPlannedAccesses, searchValue, category])

        // Handle selection change
        const handleValueChange = useCallback(
            (selectedId: string) => {
                if (listType === 'all') {
                    const foundAccessType = allCategoryAccessTypes?.find(accessType => accessType.id === selectedId)
                    if (foundAccessType) {
                        onChange(foundAccessType)
                    }
                } else {
                    const accessType = plannedAccesses?.find(plannedAccess => plannedAccess.id === selectedId)
                    if (accessType) {
                        onChange({
                            id: accessType.id,
                            accessType: accessType.accessType,
                            category: accessType.category
                        })
                    }
                }
            },
            [listType, plannedAccesses, onChange, allAccessTypes]
        )

        // Get access types
        const accessTypes = useMemo((): Pick<PlannedAccesses, 'accessType'>[] => {
            if (listType == 'planned') {
                return (
                    plannedAccesses?.map(plannedAccess => ({
                        accessType: plannedAccess
                    })) ?? []
                )
            }

            return (
                allCategoryAccessTypes?.map(accessType => ({
                    accessType
                })) ?? []
            )
        }, [listType, plannedAccesses, allAccessTypes, searchValue])

        // Get loading
        const isLoading = useMemo(() => {
            return isLoadingAllPlannedAccesses || isLoadingAllAccessTypes
        }, [isLoadingAllPlannedAccesses, isLoadingAllAccessTypes])

        // Get error
        const isError = useMemo(() => {
            return isErrorAllPlannedAccesses || isErrorAllAccessTypes
        }, [isErrorAllPlannedAccesses, isErrorAllAccessTypes])

        // Set list type when projectId is provided
        useEffect(() => {
            if (projectId) {
                setListType('planned')
            } else {
                setListType('all')
            }
        }, [projectId])

        // Update popover open state
        const handleSetOpen = useCallback(
            (isOpen: boolean) => {
                setIsPopoverOpen(isOpen)
                setIsQueryEnabled(isOpen)
                if (!isOpen) {
                    onResetSearch(inputUserRef)
                }
            },
            [onResetSearch]
        )

        return (
            <Flexbox direction="column" gap={1.5} fullWidth>
                <ComboboxProvider setValue={onSearch} resetValueOnHide>
                    <SelectProvider value={value?.id ?? ''} setValue={handleValueChange} setOpen={handleSetOpen}>
                        {label && <Label>{label}</Label>}
                        <SelectTrigger
                            ref={ref}
                            placeholder={placeholder}
                            fullWidth
                            value={value?.id}
                            isLoading={isPopoverOpen && isLoading}
                            displayValue={() =>
                                value && (
                                    <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                        {t(eventTypeMapToLabel[value.accessType])}
                                        {onClear && (
                                            <XCloseIcon
                                                role="button"
                                                onClick={e => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    onClear()
                                                }}
                                            />
                                        )}
                                    </Flexbox>
                                )
                            }
                            hasError={!!errorMessage}
                            disabled={disabled}
                        />

                        {errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}

                        <SelectComboboxPopover>
                            {projectId && (
                                <ToggleGroupListType
                                    listType={listType}
                                    setListType={setListType}
                                    radioList={{
                                        planned: {label: 'projects:planned_accesses', value: 'planned'},
                                        all: {label: 'commons:view_all', value: 'all'}
                                    }}
                                />
                            )}
                            <SelectComboboxInput placeholder={t('commons:search_access_type')} />
                            <SelectComboboxList>
                                {isLoading ? (
                                    Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                                ) : isError ? (
                                    <InputHelpText error={errorMessage || t('errors:default')} />
                                ) : accessTypes.length > 0 ? (
                                    <SelectVirtualList
                                        data={accessTypes}
                                        totalCount={accessTypes.length}
                                        overscan={100}
                                        itemContent={(itemIndex: number, item: Pick<PlannedAccesses, 'accessType'>) => (
                                            <SelectItem
                                                hasItemCheckbox
                                                key={itemIndex}
                                                value={item.accessType.id}
                                                disabled={
                                                    disabledAccessTypes?.includes(item.accessType) ||
                                                    disabledAccessTypeKeyNames?.includes(item.accessType.accessType)
                                                }
                                            >
                                                {t(eventTypeMapToLabel[item.accessType.accessType])}
                                            </SelectItem>
                                        )}
                                    />
                                ) : (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </SelectProvider>
                </ComboboxProvider>
            </Flexbox>
        )
    }
)
